import { Area, IGeofence, UserAttributeDefs } from '@iotv/datamodel-core'
import { Position, PossibleBusinessObject, TimedPosition } from '@iotv/iotv-v3-types'
import { EFFLUENT_PATH_TYPE, EffluentPath } from './EffluentPath'
import { EffluentTracker, MoistureReading, allAreaData } from './EffluentTracker'
import { PivotEffluentSpreader } from './PivotEffluentSpreader'
import { LateralEffluentSpreader } from './LateralEffluentSpreader'
import { TravellingEffluentSpreader } from './TravellingEffluentSpreader'
import { SpreadingGun } from './SpreadingGun'

const dev = process.env.REACT_APP_DEBUG
const debug = dev && false

const typeName = 'SpreadingEvent'

export type SpreadingEventType = ISpreadingEvent & Omit<PossibleBusinessObject, 'type'> & Partial<IGeofence>

export interface ISpreadingEvent {
  path: TimedPosition[] // traversed path
  irrigatedArea: Position[] // covered area
  startTime: number
  endTime: number
  spreaderName: string
  spreaderID: string
  spreaderPK?: string
  output: number
  duration: number
  distance: number
  area: number
  overlappedAreas: string[]
  mappedRun?: string //pk of run
  lastIrrigated?: number
  runDifference?: number
  pressure?: number
  applicationDepth?: number // support for old spreading events
  applicationDepthMM?: number
  pumpState: boolean[]
  pressureList: number[]
  moistureReadingsList?: MoistureReading[]
  soilRisk?: string
  type: string
}

export class SpreadingEvent extends Area implements ISpreadingEvent {
  constructor(ob: SpreadingEventType) {
    super({ ...ob, type: ob.type ?? typeName })
    this.path = ob.path
    this.points = ob.irrigatedArea
    this.irrigatedArea = ob.irrigatedArea
    this.output = ob.output
    this.duration = ob.duration
    this.startTime = ob.startTime
    this.endTime = ob.endTime
    this.spreaderName = ob.spreaderName
    this.spreaderID = ob.spreaderID
    this.spreaderPK = ob.spreaderPK
    this.distance = ob.distance
    this.area = ob.area
    this.overlappedAreas = ob.overlappedAreas
    // this.filteredPath = ob.filteredPath;
    // this.mappedPath = ob.mappedPath;
    this.runDifference = ob.runDifference
    this.pressure = ob.pressure
    this.applicationDepth = ob.applicationDepth
    this.applicationDepthMM = ob.applicationDepthMM ? ob.applicationDepthMM : ob.applicationDepth ? ob.applicationDepth * 10 : 0
    this.pumpState = ob.pumpState || []
    this.pressureList = ob.pressureList
    this.moistureReadingsList = ob.moistureReadingsList
    this.soilRisk = ob.soilRisk || 'Unknown'
    this.type = 'SpreadingEvent'
  }
  path: TimedPosition[] // traversed path
  irrigatedArea: Position[] // covered area
  startTime: number
  endTime: number
  spreaderName: string
  spreaderID: string
  spreaderPK?: string

  output: number
  distance: number
  duration: number
  area: number
  overlappedAreas: string[]
  filteredPath?: Position[]
  // filteredIrrigatedArea?: Position[];
  // mappedPath?: Position[];
  // filteredMappedArea?: Position[];
  mappedRun?: string //pk of run
  lastIrrigated?: number
  runDifference?: number
  pressure?: number
  applicationDepth?: number
  pumpState: boolean[]
  pressureList: number[]
  moistureReadingsList?: MoistureReading[]
  soilRisk: string

  _areaPointsMapping?: allAreaData

  getCoverageAndArea(spreaderList: EffluentTracker[], effluentRuns?: EffluentPath[]): Position[] {
    let useLogic = this._areaPointsMapping
    if (dev || useLogic === undefined) {
      let matchedSpreader = spreaderList.find((spreader) => spreader.id === this.spreaderID)
      if (matchedSpreader === undefined) {
        return this.irrigatedArea
      }

      let shallowCopy: EffluentTracker

      const data = { ...matchedSpreader, path: this.path }
      switch (matchedSpreader.type) {
        case 'PivotEffluentSpreader':
          shallowCopy = new PivotEffluentSpreader(data)
          break
        case 'LateralEffluentSpreader':
          shallowCopy = new LateralEffluentSpreader(data)
          break
        case 'TravellingEffluentSpreader':
          shallowCopy = new TravellingEffluentSpreader(data)
          break
        case 'SpreadingGun':
          shallowCopy = new SpreadingGun(data)
          break
        default:
          shallowCopy = new EffluentTracker(data)
      }

      let run: EffluentPath | undefined = undefined

      if (effluentRuns) {
        const expectedPathObj = shallowCopy.getExpectedRun(effluentRuns)

        if (expectedPathObj) {
          run = expectedPathObj.run
        }
      }
      let runRes = shallowCopy.getSpreadOfAllRuns(run)
      this._areaPointsMapping = runRes
      useLogic = runRes
    }

    return useLogic?.mapped?.positions || useLogic?.filtered?.positions || useLogic?.raw?.positions || this.irrigatedArea
  }

  protected recalculateWithoutContext(areas: Area[], runs: EffluentPath[]): SpreadingEvent {
    return this
  }

  /**
   * Update the current spreading event with new details
   * @param event
   */
  protected updateCurrentEventWithNewEvent(event: SpreadingEventType) {
    this.irrigatedArea = event.irrigatedArea
    this.output = event.output
    this.area = event.area
    this.overlappedAreas = event.overlappedAreas
    this.mappedRun = event.mappedRun
    this.runDifference = event.runDifference
    // this.pressure = event.pressure;
    this.applicationDepthMM = event.applicationDepthMM
    this.startTime = event.startTime
    this.endTime = event.endTime
    this.duration = event.duration
  }

  protected recalculateWithPK(tracker: EffluentTracker, areas: Area[], runs: EffluentPath[], lastTime: number): SpreadingEvent {
    let spreader: EffluentTracker

    let temp = { ...tracker, path: this.path, name: tracker.name }
    switch (tracker.type) {
      case 'LateralEffluentSpreader':
        spreader = new LateralEffluentSpreader(temp)
        break
      case 'TravellingEffluentSpreader':
        spreader = new TravellingEffluentSpreader(temp)
        break
      case 'PivotEffluentSpreader':
        // need to know if pivot on lateral or effluent
        spreader = new PivotEffluentSpreader(temp)

      case 'SpreadingGun':
        spreader = new SpreadingGun(temp)
        let bestMatched = spreader.getBestMatchedRun(runs)

        if (bestMatched) {
          switch (bestMatched.run.pathType) {
            case EFFLUENT_PATH_TYPE.LateralEffluentSpreader:
              spreader = new LateralEffluentSpreader({ ...spreader })
              break
            case EFFLUENT_PATH_TYPE.PivotEffluentSpreader:
              spreader = new PivotEffluentSpreader({ ...spreader })
              break
          }
        }
        break
      default:
        spreader = new EffluentTracker(temp)
    }

    spreader.updateMappedRun(runs, lastTime)
    const { Spreading } = spreader.createSpreadingEventAndDeadZone(areas, runs)

    this.updateCurrentEventWithNewEvent(Spreading)
    return this
    // return { ...this, ...Spreading, spreaderPK: spreader.pk };
  }

  recalculateSpreadingEvent(effluentSpreaders: EffluentTracker[] = [], areas: Area[] = [], runs: EffluentPath[] = []): SpreadingEvent {
    // check if DOES NOT have pk. calculate the expected PK of the ID
    let lastTime = Date.now()
    if (this.path !== undefined) {
      lastTime = this.path[this.path.length - 1].timestamp
    } else {
      console.log('SE: No path found!', this.pk)
    }
    // if (this.spreaderPK === undefined || this.spreaderPK === '') {
    let found = effluentSpreaders.find((spread) => spread.id === this.spreaderID)
    // let found = effluentSpreaders.find((spread) => spread.name === this.spreaderName);

    if (found !== undefined) {
      this.spreaderPK = found.pk
      // this.spreaderID = found.id;
    }

    if (this.spreaderPK !== undefined) {
      let found = effluentSpreaders.find((spread) => spread.pk === this.spreaderPK)
      if (found !== undefined) {
        return this.recalculateWithPK(found, areas, runs, lastTime)
      }
    }
    // return this;

    return this.recalculateWithoutContext(areas, runs)
  }

  getUserAttributes(): UserAttributeDefs {
    return {
      ...super.getUserAttributes(),
      name: {
        type: 'String',
        editable: true,
        label: 'Name',
        name: 'name',
        description: 'identification',
        validation: {
          regex: /^[\p{L}0-9\s']+$/u,
          pass: false,
          invalidMessage: 'Name cannot contain invalid characters',
        },
      },
      output: {
        type: 'Number',
        editable: false,
        label: 'Output (L)',
        name: 'output',
        description: 'Flow output of run',
      },
      duration: {
        type: 'Number',
        editable: false,
        label: 'Duration (min)',
        name: 'duration',
        description: 'Effluent Spreading Duration',
      },
      description: {
        type: 'String',
        editable: true,
        label: 'Description',
        description: 'description',
      },
      distance: {
        type: 'Number',
        editable: false,
        label: 'Distance (m)',
        name: 'distance',
        description: 'Distance the effluent spreader travelled',
      },
      concentration: {
        type: 'Number',
        editable: false,
        label: 'Concentration (L/m^2)',
        name: 'concentration',
        description: 'Concentrate of effluent for this run',
      },
      pressure: {
        type: 'Number',
        editable: false,
        label: 'Pressure (KPa)',
        name: 'Pressure',
        description: 'Pressure (KPa) of the fluid ',
      },
    }
  }
}
